import axios, { AxiosError } from 'axios';
import i18n from '../i18n';

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly errorCode?: string;

  constructor(status: number, detail: string, errorCode?: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.errorCode = errorCode;
  }
}

// Empty/unset -> relative same-origin requests ('/api/...'), which is the
// production setup (reverse proxy path-routes /api on the same host). Never
// default to a localhost URL here — that ships a bundle whose every API call
// fails in production (see #270).
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Routes that should NEVER be force-redirected to /login on a 401 response.
// A 401 on /api/auth/me while sitting on any of these is expected (the user
// simply isn't logged in yet) and must not boot them out of the page they
// navigated to.
// The enrollment screen the 2FA_SETUP_REQUIRED interceptor bounces back to.
// Named once so the redirect target, the "already there" guard and the public-path
// list cannot drift apart from each other (or from the route in App.tsx).
export const TWO_FA_SETUP_PATH = '/2fa-setup';

const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  TWO_FA_SETUP_PATH,
  '/2fa-verify',
  '/join',
  '/welcome',
];

function isOnPublicPath(pathname: string): boolean {
  // Spec 025: bare `/` shows the Welcome screen for guests — don't bounce 401s
  // from the initial auth probe to /login.
  if (pathname === '/') return true;
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Include cookies in cross-origin requests
  // Serialize array params as repeated `key=v1&key=v2` (FastAPI style),
  // not the default `key[]=v1&key[]=v2` which FastAPI ignores.
  paramsSerializer: { indexes: null },
  // No default Content-Type. axios emits application/json for plain object
  // bodies and multipart/form-data (with boundary) for FormData bodies. Setting
  // a default here breaks FormData uploads — see PhotoChallengeForm.
});

// FastAPI's 422 Unprocessable Entity returns `detail` as an array of
// `{type, loc, msg, input, ctx}` validation errors, not a string. Other
// status codes typically return `detail` as a string. ApiError.detail is
// always a string for renderers — flatten the array into one human-readable
// message here so callers can `setError(err.detail)` without crashing React.
type ValidationErrorItem = {
  type?: string;
  msg?: string;
  loc?: (string | number)[];
  ctx?: { min_length?: number; max_length?: number; [key: string]: unknown };
};
type ErrorPayload = { detail?: string | ValidationErrorItem[]; error_code?: string };

// FastAPI 422 items carry an English, un-localized `msg` straight from Pydantic
// (e.g. "value is not a valid email address: ...") with the raw field name glued
// in front. Map the Pydantic error `type` to a translated, self-contained
// message instead of echoing `msg` — the UI is localized, the validation
// feedback must be too (#135). Unknown types fall back to a generic string.
function localizeValidationItem(item: ValidationErrorItem): string {
  const field = Array.isArray(item?.loc)
    ? item.loc.filter((s) => s !== 'body').pop()
    : undefined;
  const ctx = item?.ctx ?? {};
  switch (item?.type) {
    case 'missing':
      return i18n.t('common:validation.required');
    case 'string_too_short':
      return i18n.t('common:validation.tooShort', { min: ctx.min_length ?? 0 });
    case 'string_too_long':
      return i18n.t('common:validation.tooLong', { max: ctx.max_length ?? 0 });
    case 'value_error':
      // email-validator surfaces as `value_error`; target the email field,
      // otherwise stay generic.
      return field === 'email' || /email/i.test(item.msg ?? '')
        ? i18n.t('common:validation.email')
        : i18n.t('common:validation.invalid');
    default:
      return i18n.t('common:validation.invalid');
  }
}

export function flattenDetail(detail: ErrorPayload['detail'], fallback: string): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // De-duplicate so two fields failing the same way (e.g. both required)
    // don't repeat the identical sentence.
    const parts = [
      ...new Set(detail.map(localizeValidationItem).filter(Boolean)),
    ];
    if (parts.length > 0) return parts.join('; ');
  }
  return fallback;
}

// Request interceptor: tell the backend which language the user is reading in.
// Mail is composed server-side and leaves the app entirely, so `Accept-Language`
// from the browser is not enough — it reflects the OS, not the language the
// person picked in Clavis (spec 045 FR-009).
apiClient.interceptors.request.use((config) => {
  const language = i18n.resolvedLanguage ?? i18n.language;
  if (language) {
    config.headers.set('X-Clavis-Language', language);
  }
  return config;
});

// Response interceptor: 401 → redirect to login (only from protected routes);
// other errors → typed ApiError
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ErrorPayload>) => {
    // A 401 from a protected route means the session is gone — bounce to login.
    // On a public path the 401 is the answer to the form the user just submitted
    // (wrong password, wrong TOTP code), so it stays on screen and must fall
    // through to the ApiError wrapping below like every other status. Rejecting
    // the raw AxiosError here made every `err instanceof ApiError` check fail,
    // so callers rendered their generic fallback instead of the real reason —
    // a wrong 2FA code read "Ein unerwarteter Fehler ist aufgetreten" (#490).
    if (error.response?.status === 401 && !isOnPublicPath(window.location.pathname)) {
      window.location.href = '/login';
    }

    // A restricted 2fa_setup token used outside the setup flow must never
    // surface as a bare 403 — route the user back into the enrollment flow
    // with an explanation instead (#322). Guarded on the error_code so only
    // the backend's coded response triggers it, and skipped when already on
    // the setup screen so in-flow errors render inline.
    //
    // A full document navigation (not a router push) is deliberate and matches
    // the 401 handler above: this interceptor lives outside the React tree and
    // has no router access, and dropping the restricted-token app state is the
    // desired outcome here anyway.
    if (
      error.response?.status === 403 &&
      error.response.data?.error_code === '2FA_SETUP_REQUIRED' &&
      window.location.pathname !== TWO_FA_SETUP_PATH
    ) {
      window.location.href = `${TWO_FA_SETUP_PATH}?reason=required`;
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      const detail = flattenDetail(data?.detail, error.message);
      const errorCode = data?.error_code;
      return Promise.reject(new ApiError(status, detail, errorCode));
    }

    // Network error or timeout
    return Promise.reject(new ApiError(0, error.message ?? 'Network error'));
  }
);

export default apiClient;

